import * as Popover from "@radix-ui/react-popover";
import { Button, ScrollArea } from "@alchemy/ui";
import { Settings, Bell02 } from "@alchemy/ui/icons/16";

import { Path } from "@util/paths";
import { analyticsEvent, EventPrefix } from "@util/analytics";

import NotificationPanelLink from "./NotificationPanelLink";
import { trpc } from "@util/trpc/trpcClient";
import useMarkNotificationsRead from "../hooks/useMarkNotificationsRead";
import { notifConfig } from "../notifConfig";
import { ViewAsPreservingLink } from "@features/common/ViewAsPreservingLink";

interface NotificationOverlayProps {
  onHide: () => void;
}

const MAX_NOTIFICATIONS = 10;

const NotificationOverlay = ({ onHide }: NotificationOverlayProps) => {
  const notifsQuery = trpc.inAppNotifications.getInAppNotifications.useQuery();
  const notifs =
    notifsQuery.data?.inAppNotifications
      .filter((n) => !n.read)
      .slice(0, MAX_NOTIFICATIONS) ?? [];

  const markReadMutation = useMarkNotificationsRead();

  return (
    <Popover.Portal>
      <Popover.Content
        className="z-10 flex flex-col rounded-xl border border-grayscale-200 bg-white shadow-gray-md"
        style={{
          width: 500,
          maxHeight: "var(--radix-popover-content-available-height)",
          maxWidth: "var(--radix-popover-content-available-width)",
        }}
        align="end"
        sideOffset={16}
        collisionPadding={8}
      >
        <Popover.Arrow className="fill-grayscale-200" />

        <header className="flex items-center justify-between gap-2 border-b border-grayscale-200 px-4 py-3 text-grayscale-900">
          <span className="text-paragraph-300-medium">New alerts</span>
        </header>

        <ScrollArea className="min-h-0">
          <div className="overflow-hidden">
            {notifs.map((notif) => {
              const Icon = notifConfig[notif.type].icon;

              return (
                <NotificationPanelLink
                  key={notif.id}
                  to={notifConfig[notif.type].actionLink}
                  onDelete={() => {
                    markReadMutation.mutate(
                      { inAppNotificationIds: [notif.id] },
                      {
                        onSuccess: () => {
                          analyticsEvent(
                            `${EventPrefix.Alerts}: Deleted alert from notification panel`,
                            { type: notif.type },
                          );
                        },
                      },
                    );
                  }}
                  onClick={onHide}
                >
                  <div className="flex gap-1">
                    <Icon className="mt-1 flex-shrink-0 text-grayscale-700" />

                    <span className="text-paragraph-200-regular text-grayscale-800">
                      {notif.body}
                    </span>
                  </div>
                </NotificationPanelLink>
              );
            })}
          </div>
        </ScrollArea>

        <footer className="flex items-center justify-between gap-1 border-t border-grayscale-200 px-4 py-3">
          <Button
            intent="tertiary"
            size="sm"
            icon={<Settings />}
            as={ViewAsPreservingLink}
            to={Path.ConfigureAlerts}
            onClick={onHide}
          />
          <Button
            intent="tertiary"
            size="sm"
            onClick={() => {
              markReadMutation.mutate(
                { inAppNotificationIds: notifs.map((n) => n.id) },
                {
                  onSuccess: () => {
                    analyticsEvent(
                      `${EventPrefix.Alerts}: Cleared alerts from notification panel`,
                    );
                  },
                },
              );

              onHide();
            }}
          >
            Dismiss all
          </Button>

          <Button
            intent="primary"
            size="sm"
            icon={<Bell02 />}
            as={ViewAsPreservingLink}
            to={Path.AlertsHub}
            className="ml-auto"
            onClick={() => {
              analyticsEvent(`${EventPrefix.Alerts}: Clicked alerts hub link`);
              onHide();
            }}
          >
            View all alerts
          </Button>
        </footer>
      </Popover.Content>
    </Popover.Portal>
  );
};

export default NotificationOverlay;
